import { Add, ArrowBack, AttachFile, FileCopy } from "@mui/icons-material";
import { Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from 'dayjs';
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { complaintEdit, permissionsAtom, userTypeAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { ComplaintTimelineInternal } from "../../components/complaint/ComplaintTimelineInternal";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import { ComplaintService } from "../../services/ComplaintService";
import { AccountDTO } from "../../types/AccountDTO";
import { ComplaintAction } from "../../types/ComplaintActionDTO";
import { ComplaintDTO } from "../../types/ComplaintDTO";
import { PdfService } from "../../utils/PdfService";
import { useUtils } from "../../utils/utilFunctions";
import './Complaint.scss';

export const complaintPriorities = ["Crítica", "Alta", "Média", "Baixa"];
export const complaintTypes = [
	"Assédio moral",
	"Assédio sexual",
	"SST",
	"Risco Psicossocial",
	"Conduta inadequada"
];
export const criteria = [
	"Risco à integridade física",
	"Risco psicossocial",
	"Assédio",
	"Impacto legal",
	"Reincidência"
];

const Complaint: FC = () => {
	const [userType] = useAtom(userTypeAtom);
	const [complaint, setComplaint] = useAtom(complaintEdit);
	const [loading, setLoading] = useState(false);
	const [users, setUsers] = useState<AccountDTO[]>([]);
	const [permissions] = useAtom(permissionsAtom);
	const isMobile = useMediaQuery('(max-width: 1024px)');
	const {id} = useParams<{ id: string }>();
	const navigate = useNavigate();
	const service = new ComplaintService();
	const pdfService = new PdfService();
	const { handleCopy, formatStringFullDate, verifyPermission } = useUtils();
	const { showAlert, showError } = useAlert();

	const externalUser = "EXTERNO" === userType;
	const user = localStorage.getItem("username");
	const allActionsFinished = complaint && complaint.actions?.length && complaint.actions.every(action => !!action.conclusion?.trim());

	const openDocument = (id: string) => {
		window.open(`${window.location.origin}/api/documents/view/${id}`, "_blank");
	}

	const handleExportPdf = () => {
		pdfService.exportComplaintsToPdf([complaint], `denuncia_${complaint.id}`);
	}

	const handleChange = (field: keyof ComplaintDTO, value: string | ComplaintAction[]) => {
		if (field === 'criteria' && typeof value === 'string') {
			const values = (complaint.criteria?.trim().split(",") || []);
			const newValues = values?.includes(value)
				? values.filter(item => item !== value)
				: [...values, value];
			value = newValues.filter(item => !!item).join(",");
		}
		setComplaint(prev => ({
			...prev,
			[field]: value
		}));
	};

	const loadComplaint = async () => {
		const response = await service.getComplaint(id || '');
		if (response && response.data) {
			setComplaint(response.data);
		}
	};

	useEffect(() => {
		if (!!permissions.length) {
			verifyPermission(permissions, 'COMPLAINTS');
		}
	}, []);

	useEffect(() => {
		const loadUsers = async () => {
			if (!complaint.companyId || users.length > 0) return;
			const response = await service.getUsers(complaint.companyId);
			if (response && response.data) {
				setUsers(response.data);
			}
		};
		if (!complaint.id) {
			loadComplaint();
		}
		loadUsers();
	}, [complaint]);

	return (
		<div className="complaint">
			<AppBar
				modulo="Detalhes da denúncia"
			/>
			<div className="top-complaint">
				<Button
					variant="contained"
					color="success"
					startIcon={<ArrowBack />}
					onClick={() => navigate('/gestao-de-denuncias/painel')}
				>
					Voltar para denúncias
				</Button>
			</div>
			<div id="complaint-content">
				<div className="cards-wrapper">
					<div className="card complaint-infos">
						<Typography variant="h5">Informações da denúncia</Typography>
						<div className="complaint-content">
							<div className="complaint-info-line">
								<p className="bold">Protocolo</p>
								<Box display="flex" alignItems="center" flex={1}>
									<p>{complaint.id}</p>
									<IconButton onClick={() => handleCopy(complaint.id.toString())}>
										<FileCopy />
									</IconButton>
								</Box>
							</div>
							<div className="complaint-info-line">
								<p className="bold">Data / Hora</p>
								<p>{formatStringFullDate(complaint.createdAt)}</p>
							</div>
							<div className="complaint-info-line">
								<p className="bold">Empresa</p>
								<p>{complaint.companyName}</p>
							</div>
							<div className="complaint-info-line">
								<p className="bold">Setor</p>
								<p>{complaint.sector}</p>
							</div>
							<div className="complaint-info-line">
								<p className="bold">Cargo</p>
								<p>{complaint.position}</p>
							</div>
							<p className="bold">Tipo informado pelo denunciante</p>
							<p>{complaint.complaintType}</p>
							<p className="bold">Descrição</p>
							<p>{complaint.description}</p>
							<p className="bold">Anexos</p>
							{complaint.files?.split(",").map((file) => (
								<div className="file pointer" onClick={() => openDocument(file)}>
									<AttachFile/>
									{file}
								</div>
							))}
						</div>
					</div>
					<div className="right-cards">
						<div className="right-cards-content">
							<div className="classification-history">
								<div className="card classification">
									<div className="classification-title">
										<Typography variant="h5">Classificação final</Typography>
										<Typography>(pode ser alterada pelo gestor)</Typography>
									</div>
									<Box className="classification-selects">
										<FormControl size="small" sx={{width: "100%"}}>
											<InputLabel>
												Tipo
											</InputLabel>
											<Select
												disabled={!permissions.includes('CLASSIFY_COMPLAINTS') || !!complaint.finishedAt}
												label='Tipo'
												id="complaint-type"
												value={complaint.finalType || ''}
												onChange={(e) => handleChange("finalType", e.target.value)}
											>
												{complaintTypes.map(item => (
													<MenuItem
														key={item}
														value={item}
													>
														{item}
													</MenuItem>
												))}
											</Select>
										</FormControl>
										<FormControl size="small" sx={{width: "100%"}}>
											<InputLabel>
												Prioridade
											</InputLabel>
											<Select
												disabled={!permissions.includes('CLASSIFY_COMPLAINTS') || !!complaint.finishedAt}
												label='Prioridade'
												id="complaint-priority"
												value={complaint.priority || ''}
												onChange={(e) => handleChange("priority", e.target.value)}
											>
												{complaintPriorities.map(item => (
													<MenuItem
														key={item}
														value={item}
													>
														<p className={`bold ${item}`}>{item}</p>
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Box>
									<div className="criterio-container">
										<Typography variant="h6">Critérios</Typography>
										<div className="criterio-grid">
											{criteria.map((criterio) => (
												<FormControlLabel
													key={criterio}
													className="criterio-item"
													disabled={!permissions.includes('CLASSIFY_COMPLAINTS') || !!complaint.finishedAt}
													control={
														<Checkbox
															checked={complaint.criteria?.includes(criterio) || false}
															onChange={() => handleChange('criteria', criterio)}
														/>
													}
													label={criterio}
												/>
											))}
										</div>
									</div>
								</div>
								<div className="card history">
									<Typography variant="h5">Histórico</Typography>
									<ComplaintTimelineInternal history={complaint.history}/>
								</div>
							</div>
							<div className="card complaint-actions">
								<Typography variant="h5">Ações / Plano de ações</Typography>
								{complaint.actions?.length
									? <div className="actions-grid">
										<div className="action-items">
											{filteredActions().map((action) => (
												<div key={action.id} className="action-item">
													<TextField
														size="small"
														multiline
														sx={{width: isMobile ? "100%" : "30%", marginTop: "8px"}}
														value={action.description}
														label="Descrição da ação necessária"
														disabled={!!complaint.finishedAt}
														onChange={(e) => {
															const newActions = complaint.actions?.map(item =>
																item.id === action.id ? { ...item, description: e.target.value } : item
															);
															handleChange("actions", newActions || []);
														}}
													/>
													<Tooltip placement="left" title="Após salvar, o usuário responsável receberá um e-mail de notificação da ação atribuída">
														<FormControl
															size="small"
															sx={{width: isMobile ? "100%" : "30%", marginTop: "8px"}}
														>
															<InputLabel>
																Responsável
															</InputLabel>
															<Select
																disabled={!permissions.includes('CLASSIFY_COMPLAINTS') || !!complaint.finishedAt}
																label='Responsável'
																value={action.username || ''}
																onChange={(e) => {
																	const newActions = complaint.actions?.map(item =>
																		item.id === action.id
																			? { ...item, username: e.target.value, name: users.find(i => i.username === e.target.value)?.name || ''}
																			: item
																	);
																	handleChange("actions", newActions || []);
																}}
															>
																{users.map(item => (
																	<MenuItem
																		key={item.username}
																		value={item.username}
																	>
																		{item.name}
																	</MenuItem>
																))}
															</Select>
														</FormControl>
													</Tooltip>
													<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
														<DemoContainer components={['DatePicker']}>
															<DatePicker
																label="Prazo"
																value={dayjs(action.deadline, "DD/MM/YYYY") || ""}
																sx={{width: isMobile ? "100%" : "auto", minWidth: isMobile ? '100%' : "15%"}}
																disablePast
																slotProps={{textField: {size: 'small'}}}
																disabled={!permissions.includes('CLASSIFY_COMPLAINTS') || !!complaint.finishedAt}
																onChange={(e) => {
																	const newActions = complaint.actions?.map(item =>
																		item.id === action.id ? { ...item, deadline: e?.format("DD/MM/YYYY") || "" } : item
																	);
																	handleChange("actions", newActions || []);
																}}
															/>
														</DemoContainer>
													</LocalizationProvider>
													<TextField
														size="small"
														multiline
														sx={{width: isMobile ? "100%" : "30%", marginTop: "8px"}}
														value={action.conclusion}
														label="Conclusão da ação"
														disabled={!!complaint.finishedAt}
														onChange={(e) => {
															const newActions = complaint.actions?.map(item =>
																item.id === action.id ? { ...item, conclusion: e.target.value } : item
															);
															handleChange("actions", newActions || []);
														}}
													/>
												</div>
											))}
										</div>
									</div>
									: <></>
								}
								{!permissions.includes('CLASSIFY_COMPLAINTS') || !!complaint.finishedAt
									? <></>
									: <Button
										id="create-action"
										onClick={() => {
											handleChange("actions", [...complaint.actions || [], {id: new Date().getTime() * -1} as ComplaintAction]);
										}}
									>
										<Add/> Adicionar ação
									</Button>
								}
							</div>
						</div>
					</div>
				</div>
				<div className="complaint-buttons">
					{!permissions.includes('EXPORT_COMPLAINTS')
						? <></>
						: <Button
							variant="contained"
							color="secondary"
							id="export-pdf"
							onClick={handleExportPdf}
						>
							Exportar denúncia
						</Button>
					}
					{!complaint.finishedAt
						? <Button
							variant="contained"
							color="success"
							onClick={save}
						>
							{loading
								? <CircularProgress sx={{color: "#292929ff"}} size={'25px'}/>
								: allActionsFinished ? "Finalizar" : "Salvar"
							}
						</Button>
						: <></>
					}
					</div>
			</div>
		</div>
	);

	async function save() {
		setLoading(true);
		const response = await service.save(complaint);
		if (!response || response.status !== 200) {
			showError("Erro ao salvar as informações da denúncia");
		} else {
			showAlert("Informações da denúncia salvas com sucesso!");
			loadComplaint();
		}
		setLoading(false);
	}

	function filteredActions() {
		if (!externalUser) {
			return complaint.actions
		}
		return complaint.actions?.filter(action => action.name === user);
	}
}

export default Complaint;